import { DialogComponent } from '@theme/dialog';

/**
 * A slide-out drawer with a "notify me when back in stock" form for a sold-out
 * product variant. Submitting the form is a genuine Shopify contact form
 * submission (full page request), sending a message to the shop owner - no
 * custom submit handling needed. After a successful signup, it also loads a
 * "you may also like" upsell by reusing whichever product-recommendations
 * section already exists on the page as the data source, instead of
 * duplicating that section's logic.
 *
 * @extends {DialogComponent}
 */
class NotifyDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    // Re-opens automatically after the native contact form redirects back here
    // with a success/error state, so the shopper sees the result of their
    // submission. Checks both the server-rendered `auto-open` attribute and
    // the `contact_posted` query param Shopify adds to the redirect URL after
    // a contact form submission - belt and braces, since the attribute alone
    // has been unreliable (relies on Liquid's `form` variable being correctly
    // scoped at the point it's read), while the query param is a guaranteed,
    // easily-inspectable signal straight from the URL itself.
    if (this.hasAttribute('auto-open') || new URLSearchParams(window.location.search).has('contact_posted')) {
      this.showDialog();
    }

    this.#loadUpsellRecommendations();
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Fetches "you may also like" recommendations for the current product and
   * injects the resulting product cards into the drawer. Only runs when the
   * upsell placeholder is present (rendered server-side only right after a
   * successful signup) and an existing <product-recommendations> element can
   * be found elsewhere on the page to borrow its configuration/data source from.
   */
  #loadUpsellRecommendations() {
    const { upsellContainer, upsellList } = this.refs;
    if (!(upsellContainer instanceof HTMLElement) || !(upsellList instanceof HTMLElement)) return;

    const productId = this.dataset.productId;
    const source = document.querySelector('product-recommendations');

    if (!productId || !(source instanceof HTMLElement) || !source.dataset.url) return;

    const url = `${source.dataset.url}&product_id=${productId}&section_id=${source.dataset.sectionId}&intent=${
      source.dataset.intent || 'related'
    }`;

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const items = Array.from(parsed.querySelectorAll('.resource-list__item')).slice(0, 4);

        if (!items.length) return;

        upsellList.replaceChildren(...items);
        upsellContainer.hidden = false;
      })
      .catch((error) => console.error('Failed to load "you may also like" recommendations', error));
  }
}

if (!customElements.get('notify-drawer-component')) {
  customElements.define('notify-drawer-component', NotifyDrawerComponent);
}
